import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { DATE_FORMATS_FNS, ReactTable, useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { combinedLicenseData } from "modules/LocationLicenseOverview/utils";
import { formatDate } from "utils/dateHelperFunctions";
import { formatPrice } from "utils/formatPrice";

import type { LicenseType } from "modules/LocationLicenseOverview";
import type { Licenses } from "modules/LocationLicenseOverview/components/LocationLicenseNavbar";
import type { LicenseItem } from "modules/LocationLicenseOverview/queryKeys";

type OneTimeTableProps = {
  items: LicenseItem[];
  isLoading: boolean;
  isLoadingItems: boolean;
  licenses?: Licenses;
  onLicenseRowClick: (rowData: LicenseType) => void;
  handleDeleteLicense: (id: number) => void;
};

const columnHelper = createColumnHelper<LicenseType>();

export const OneTimeTable = ({ items, onLicenseRowClick, handleDeleteLicense, isLoading, licenses, isLoadingItems }: OneTimeTableProps) => {
  const { t } = useTranslation();
  const canDeleteLicense = useCan(ACL_ACTIVITIES.Delete, ACL_RESOURCES.ExactLicense);

  // TODO: cleanup the mess with types and utils here, we should not need useMemo
  const oneTimeData = useMemo(() => combinedLicenseData(licenses, items, t, false), [licenses, items]);

  const handleDeleteLicenseOnClick = (e: React.MouseEvent<HTMLDivElement>, id: number) => {
    e.stopPropagation();
    handleDeleteLicense(id);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "code",
        header: t("v8_code", "Code"),
        cell: ({
          row: {
            original: { code }
          }
        }) =>
          code ? (
            <div className="license-code">{code}</div>
          ) : (
            <div className="no-license-code">
              <Icon className="link slash solid red" />
            </div>
          ),
        size: 10
      }),
      columnHelper.accessor(row => row, {
        id: "name",
        header: t("v8_name", "Name"),
        cell: ({
          row: {
            original: { description, name }
          }
        }) => <span>{description || name || ""}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "category",
        header: t("v8_role_license_service", "Role / License / Service"),
        cell: ({
          row: {
            original: { category }
          }
        }) => <span>{category}</span>,
        size: 250
      }),
      columnHelper.accessor(row => row, {
        id: "invoice_on",
        header: t("v8_invoice_on", "Invoice on"),
        cell: ({
          row: {
            original: { invoiceOn }
          }
        }) => <span>{invoiceOn ? formatDate(invoiceOn, DATE_FORMATS_FNS.dateMonthYear) : ""}</span>,
        size: 100
      }),
      columnHelper.accessor(row => row, {
        id: "quantity",
        header: t("v8_amount", "Amount"),
        cell: ({
          row: {
            original: { quantity }
          }
        }) => <span>{quantity}</span>,
        size: 100
      }),
      columnHelper.accessor(row => row, {
        id: "price",
        header: t("v8_unit_price", "Unit Price"),
        cell: ({
          row: {
            original: { price }
          }
        }) => <span>{`€ ${formatPrice(price)}`}</span>,
        size: 100
      }),
      columnHelper.accessor(row => row, {
        id: "totalPrice",
        header: t("v8_total_price", "Total Price"),
        cell: ({
          row: {
            original: { totalPrice }
          }
        }) => <span>{`€ ${formatPrice(totalPrice)}`}</span>,
        size: 100
      }),
      columnHelper.accessor(row => row, {
        id: "actions",
        header: "",
        cell: ({
          row: {
            original: { categoryID, id }
          }
        }) =>
          canDeleteLicense && !categoryID && id ? (
            <div className="subitem-delete">
              <div>
                <Icon className="trash red" onClick={(e: React.MouseEvent<HTMLDivElement>) => handleDeleteLicenseOnClick(e, id)} />
              </div>
            </div>
          ) : null,
        size: 50
      })
    ],
    [t, handleDeleteLicenseOnClick, canDeleteLicense]
  );

  return (
    <div className="MonthlyTable mb-25">
      <ReactTable
        isFetching={isLoading || isLoadingItems}
        columns={columns}
        data={oneTimeData}
        emptyRowMessage={t("v8_no_one_time_licenses", "No one time licenses")}
        onRowClick={onLicenseRowClick}
      />
    </div>
  );
};
